"use client";

import * as React from "react";

const MARKER = "__dappUiOverlay";

type Entry = { close: () => void };

/**
 * 열려 있는 오버레이 스택(LIFO). 모듈 단위로 공유한다 — 백 제스처는 항상
 * "가장 위" 오버레이 하나만 닫아야 하는데, 훅 인스턴스는 서로를 모른다.
 */
const stack: Entry[] = [];

/** 우리가 밀어 넣은 history 엔트리 수. 항상 `stack.length`로 수렴시킨다. */
let ourEntries = 0;

/** 우리가 스스로 유발한 뒤로가기 — 이 popstate는 사용자 제스처가 아니다. */
let pendingSelfBack = 0;

let listening = false;
let reconcileScheduled = false;

function handlePopState() {
  if (pendingSelfBack > 0) {
    pendingSelfBack -= 1;
    return;
  }
  ourEntries = Math.max(0, ourEntries - 1);
  stack.pop()?.close();
  stopListeningIfIdle();
}

function ensureListening() {
  if (listening) return;
  window.addEventListener("popstate", handlePopState);
  listening = true;
}

function stopListeningIfIdle() {
  if (stack.length > 0 || !listening) return;
  window.removeEventListener("popstate", handlePopState);
  listening = false;
}

/**
 * 엔트리 수를 스택 깊이에 맞춘다. **같은 tick의 변화가 모두 끝난 뒤** 한 번만
 * 실행되는 것이 핵심 — 오버레이 A를 닫고 곧바로 B를 여는 흐름(Get ONEUSD의
 * 액션 선택 → Swap 화면)에서는 깊이가 그대로 1이므로 A의 엔트리를 B가 그냥
 * 물려받고, 되돌리기(back)가 아예 일어나지 않는다. 닫을 때 즉시 back()을 하면
 * 그 back이 B가 밀어 넣은 엔트리를 때린다.
 */
function scheduleReconcile() {
  if (reconcileScheduled) return;
  reconcileScheduled = true;
  queueMicrotask(() => {
    reconcileScheduled = false;
    const surplus = ourEntries - stack.length;
    if (surplus <= 0) {
      stopListeningIfIdle();
      return;
    }
    ourEntries -= surplus;
    pendingSelfBack += 1; // go(-n)은 popstate를 한 번 발생시킨다.
    window.history.go(-surplus);
    stopListeningIfIdle();
  });
}

/**
 * Lets the platform's own "go back" gesture dismiss an overlay: Android's
 * hardware/system back button, iOS Safari's back-swipe, and the browser Back
 * button all emit `popstate`, which none of radix/vaul handle — without this the
 * back button leaves the host page instead of closing the modal on top of it.
 *
 * 열려 있는 동안 history 엔트리 하나를 차지하고 공유 스택에 올라간다. 백 제스처는
 * 최상위 오버레이 하나만 닫고, UI로 닫으면(X·오버레이 클릭·Esc) 그 엔트리를
 * 반납해 히스토리가 원래 자리로 돌아온다.
 */
export function useHistoryDismiss(open: boolean, close: () => void): void {
  const closeRef = React.useRef(close);
  closeRef.current = close;

  React.useEffect(() => {
    if (!open || typeof window === "undefined") return undefined;
    return pushOverlay(() => closeRef.current());
  }, [open]);
}

/**
 * 훅의 알맹이 — React 없이 동작하므로 그대로 테스트할 수 있다.
 * 등록하고, 해제 함수를 돌려준다.
 */
export function pushOverlay(close: () => void): () => void {
  const entry: Entry = { close };
  stack.push(entry);
  if (ourEntries < stack.length) {
    window.history.pushState({ [MARKER]: true }, "");
    ourEntries += 1;
  }
  ensureListening();

  return () => {
    const index = stack.lastIndexOf(entry);
    if (index === -1) return; // 이미 백 제스처로 닫힌 엔트리.
    stack.splice(index, 1);
    scheduleReconcile();
  };
}
